import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { RootAppSidebar } from '@affine/core/components/root-app-sidebar';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import {
  AppSidebarFallback,
  OpenInAppCard,
} from '@affine/core/modules/app-sidebar/views';
import { AppTabsHeader } from '@affine/core/modules/app-tabs-header';
import { NavigationButtons } from '@affine/core/modules/navigation';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { setStreamLainMotionPolicy } from '@blocksuite/affine/shared/utils';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  useLayoutEffect,
} from 'react';

import { useDesktopAppLayoutReady } from '../../bootstrap/use-desktop-app-layout-ready';
import * as styles from './styles.css';

export const AppContainer = ({
  children,
  bootSplashReady,
  className,
  fallback = false,
  workspaceNavigation = true,
  ...rest
}: PropsWithChildren<{
  bootSplashReady?: boolean;
  className?: string;
  fallback?: boolean;
  workspaceNavigation?: boolean;
}>) => {
  useDesktopAppLayoutReady(bootSplashReady ?? !fallback);
  const { appSettings } = useAppSettingHelper();
  useLayoutEffect(() => {
    setStreamLainMotionPolicy(
      appSettings.enableAnimations,
      appSettings.enableCanvasAnimations
    );
    document.documentElement.dataset.streamlainAnimations =
      appSettings.enableAnimations ? 'on' : 'off';
  }, [appSettings.enableAnimations, appSettings.enableCanvasAnimations]);

  const noisyBackground =
    BUILD_CONFIG.isElectron && appSettings.enableNoisyBackground;
  const blurBackground =
    BUILD_CONFIG.isElectron &&
    environment.isMacOs &&
    appSettings.enableBlurBackground;
  return (
    <div
      {...rest}
      className={clsx(styles.appStyle, className, {
        'noisy-background': noisyBackground,
        'blur-background': blurBackground,
      })}
      data-noise-background={noisyBackground}
      data-translucent={blurBackground}
    >
      <LayoutComponent
        fallback={fallback}
        workspaceNavigation={workspaceNavigation}
      >
        {children}
      </LayoutComponent>
    </div>
  );
};

// Lifecycle invariant: RootAppSidebar must remain a stable sibling of
// MainContainer. Its existing Organize subtree participates in local-first
// cache hydration and boot readiness, so visual pane state must never reparent
// or conditionally remount this tree without an explicit lifecycle review.
const DesktopLayout = ({
  children,
  fallback = false,
  workspaceNavigation = true,
}: PropsWithChildren<{
  fallback?: boolean;
  workspaceNavigation?: boolean;
}>) => {
  const workspaceService = useServiceOptional(WorkspaceService);
  const isInWorkspace = !!workspaceService;
  return (
    <div className={styles.desktopAppViewContainer}>
      <div className={styles.desktopTabsHeader}>
        <AppTabsHeader
          left={
            <>{isInWorkspace && workspaceNavigation && <NavigationButtons />}</>
          }
        />
      </div>
      <div className={styles.desktopAppViewMain}>
        {fallback ? (
          <AppSidebarFallback />
        ) : (
          isInWorkspace && workspaceNavigation && <RootAppSidebar />
        )}
        <MainContainer>{children}</MainContainer>
      </div>
    </div>
  );
};

const BrowserLayout = ({
  children,
  fallback = false,
  workspaceNavigation = true,
}: PropsWithChildren<{
  fallback?: boolean;
  workspaceNavigation?: boolean;
}>) => {
  const workspaceService = useServiceOptional(WorkspaceService);
  const isInWorkspace = !!workspaceService;

  return (
    <div className={styles.browserAppViewContainer}>
      <OpenInAppCard />
      {fallback ? (
        <AppSidebarFallback />
      ) : (
        isInWorkspace && workspaceNavigation && <RootAppSidebar />
      )}
      <MainContainer>{children}</MainContainer>
    </div>
  );
};

const LayoutComponent = BUILD_CONFIG.isElectron ? DesktopLayout : BrowserLayout;

const MainContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function MainContainer({ className, children, ...props }, ref): ReactElement {
  const workspaceService = useServiceOptional(WorkspaceService);
  const isInWorkspace = !!workspaceService;
  const { appSettings } = useAppSettingHelper();
  const appSidebarService = useService(AppSidebarService).sidebar;
  const open = useLiveData(appSidebarService.open$);

  return (
    <div
      {...props}
      className={clsx(styles.mainContainerStyle, className)}
      data-is-desktop={BUILD_CONFIG.isElectron}
      data-transparent={false}
      data-client-border={appSettings.clientBorder}
      data-side-bar-open={open && isInWorkspace}
      data-testid="main-container"
      ref={ref}
    >
      {children}
    </div>
  );
});

MainContainer.displayName = 'MainContainer';
